// src/context/PrivacyAuthContext.jsx - Complete Implementation
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth as firebaseAuth } from '../services/config/firebase';

// Privacy-First Authentication Class
class PrivacyFirstAuth {
  constructor() {
    this.storageKey = 'vibe_user_identity';
    this.settingsKey = 'vibe_user_settings';
    this.currentUser = null;
  }

  /**
   * Generate device fingerprint (privacy-friendly)
   */
  generateDeviceFingerprint() {
    const components = [
      screen.width,
      screen.height,
      new Date().getTimezoneOffset(),
      navigator.language,
      navigator.platform.slice(0, 10), // Limit platform info
      (navigator.hardwareConcurrency || 1).toString(),
    ];
    
    return btoa(components.join('|')).slice(0, 16);
  }

  /**
   * Generate secure backup code
   */
  generateBackupCode() {
    const words = [
      'ocean', 'mountain', 'forest', 'river', 'star', 'moon', 'sun', 'cloud',
      'breeze', 'thunder', 'rainbow', 'crystal', 'phoenix', 'dragon', 'wolf',
      'eagle', 'tiger', 'butterfly', 'flower', 'tree', 'stone', 'flame',
      'wisdom', 'brave', 'bright', 'swift', 'calm', 'free', 'pure', 'strong',
    ];
    
    const code = [];
    for (let i = 0; i < 4; i++) {
      const randomWord = words[Math.floor(Math.random() * words.length)];
      const randomNumber = Math.floor(Math.random() * 99) + 1;
      code.push(randomWord + randomNumber);
    }
    
    return code.join('-');
  }

  /**
   * Secure hashing function
   */
  async hashString(input) {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Create anonymous user account
   */
  async createAnonymousAccount(username, passphrase = null) {
    try {
      // Check if username already exists on this device
      const existingUser = localStorage.getItem(this.storageKey);
      if (existingUser) {
        const userData = JSON.parse(existingUser);
        if (userData.username === username) {
          throw new Error('Username already exists on this device');
        }
      }

      const deviceFingerprint = this.generateDeviceFingerprint();
      const userId = await this.hashString(username + deviceFingerprint + Date.now());
      const backupCode = this.generateBackupCode();
      
      let passphraseHash = null;
      if (passphrase) {
        passphraseHash = await this.hashString(passphrase + userId);
      }

      const userAccount = {
        userId,
        username,
        passphraseHash,
        backupCode,
        createdAt: new Date().toISOString(),
        deviceFingerprint,
        accountType: 'anonymous',
        lastLogin: new Date().toISOString(),
      };

      // Store user account
      localStorage.setItem(this.storageKey, JSON.stringify(userAccount));
      
      // Store user settings separately
      const userSettings = {
        theme: 'dark',
        notifications: true,
        privacy: {
          analytics: false,
          crashReports: true,
        },
      };
      localStorage.setItem(this.settingsKey, JSON.stringify(userSettings));

      this.currentUser = userAccount;

      return {
        success: true,
        userId,
        username,
        backupCode,
        hasPassphrase: !!passphrase,
        message: 'Account created! Save your backup code somewhere safe.',
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Quick setup without passphrase (for immediate use)
   */
  async quickSetup(username) {
    return this.createAnonymousAccount(username, null);
  }

  /**
   * Add passphrase to existing account
   */
  async addPassphrase(passphrase) {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) {
        throw new Error('No account found');
      }

      const account = JSON.parse(stored);
      const passphraseHash = await this.hashString(passphrase + account.userId);
      
      account.passphraseHash = passphraseHash;
      account.passphraseAddedAt = new Date().toISOString();
      
      localStorage.setItem(this.storageKey, JSON.stringify(account));
      this.currentUser = account;

      return {
        success: true,
        message: 'Passphrase added successfully!',
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Login with username and passphrase (if set)
   */
  async login(username, passphrase = null) {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) {
        throw new Error('No account found on this device');
      }

      const account = JSON.parse(stored);
      
      if (account.username !== username) {
        throw new Error('Username not found');
      }

      // If account has passphrase, verify it
      if (account.passphraseHash && passphrase) {
        const expectedPassphraseHash = await this.hashString(passphrase + account.userId);
        if (account.passphraseHash !== expectedPassphraseHash) {
          throw new Error('Incorrect passphrase');
        }
      } else if (account.passphraseHash && !passphrase) {
        throw new Error('Passphrase required');
      }

      // Update last login
      account.lastLogin = new Date().toISOString();
      localStorage.setItem(this.storageKey, JSON.stringify(account));
      this.currentUser = account;

      return {
        success: true,
        userId: account.userId,
        username: account.username,
        hasPassphrase: !!account.passphraseHash,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Auto-login if no passphrase is set
   */
  async autoLogin() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return { success: false, error: 'No account found' };

      const account = JSON.parse(stored);
      
      // Auto-login only if no passphrase is set
      if (!account.passphraseHash) {
        this.currentUser = account;
        return {
          success: true,
          userId: account.userId,
          username: account.username,
          hasPassphrase: false,
        };
      }

      return { success: false, error: 'Passphrase required' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if user is logged in
   */
  isLoggedIn() {
    return !!this.currentUser || !!localStorage.getItem(this.storageKey);
  }

  /**
   * Get current user info
   */
  getCurrentUser() {
    if (this.currentUser) return this.currentUser;
    
    const stored = localStorage.getItem(this.storageKey);
    if (!stored) return null;
    
    const account = JSON.parse(stored);
    this.currentUser = account;
    return account;
  }

  /**
   * Update user settings
   */
  async updateSettings(newSettings) {
    try {
      const currentSettings = this.getSettings();
      const updatedSettings = { ...currentSettings, ...newSettings };
      localStorage.setItem(this.settingsKey, JSON.stringify(updatedSettings));
      
      return { success: true, settings: updatedSettings };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
// Fix for PrivacyAuthContext.jsx - Prevent data loss during username save
// Update the updateUserProfile method to preserve existing gamification data

/**
 * FIXED: Update user profile without losing gamification data
 */
async updateUserProfile(updates) {
  try {
    console.log('👤 Updating user profile:', updates);
    
    if (!this.currentUser) {
      throw new Error('No user logged in');
    }

    // CRITICAL FIX: Save current gamification data before profile update
    let existingGamificationData = null;
    try {
      const {userId} = this.currentUser;
      const gamificationKey = `vibeGamificationData_${userId}`;
      const savedData = localStorage.getItem(gamificationKey);
      if (savedData) {
        existingGamificationData = JSON.parse(savedData);
        console.log('💾 Preserving existing gamification data:', {
          totalXP: existingGamificationData.totalXP,
          currentLevel: existingGamificationData.currentLevel,
          completedModules: existingGamificationData.completedModules?.length || 0,
        });
      }
    } catch (error) {
      console.warn('⚠️ Could not preserve gamification data:', error);
    }

    // Update user profile
    const updatedUser = {
      ...this.currentUser,
      ...updates,
      lastUpdated: new Date().toISOString(),
    };

    // Save updated profile
    localStorage.setItem(this.storageKey, JSON.stringify(updatedUser));
    this.currentUser = updatedUser;

    // CRITICAL FIX: Restore gamification data after profile update
    if (existingGamificationData) {
      const {userId} = this.currentUser;
      const gamificationKey = `vibeGamificationData_${userId}`;
      localStorage.setItem(gamificationKey, JSON.stringify(existingGamificationData));
      console.log('✅ Restored gamification data after profile update');
      
      // Force refresh gamification context
      setTimeout(() => {
        if (window.__vibeGamificationContext__ && typeof window.__vibeGamificationContext__.refreshGamificationData === 'function') {
          console.log('🔄 Forcing gamification context refresh');
          window.__vibeGamificationContext__.refreshGamificationData();
        }
      }, 100);
    }

    // Notify listeners of user update
    this.notifyListeners();
    
    console.log('✅ User profile updated successfully');
    return updatedUser;
  } catch (error) {
    console.error('❌ Failed to update user profile:', error);
    throw error;
  }
}
  /**
   * Get user settings
   */
  getSettings() {
    const stored = localStorage.getItem(this.settingsKey);
    if (!stored) {
      return {
        theme: 'dark',
        notifications: true,
        privacy: {
          analytics: false,
          crashReports: true,
        },
      };
    }
    return JSON.parse(stored);
  }

  /**
   * Export user data
   */
  async exportUserData() {
    const userData = {
      // User account (excluding sensitive data)
      account: {
        username: this.currentUser?.username,
        createdAt: this.currentUser?.createdAt,
        accountType: this.currentUser?.accountType,
      },
      // App data
      assessment: localStorage.getItem(`vibe_assessment_results_${this.currentUser?.userId || 'guest'}`),
      gamification: localStorage.getItem('vibeGamificationData'),
      progress: localStorage.getItem('vibeUserProgress'),
      settings: this.getSettings(),
      // Export metadata
      exportedAt: new Date().toISOString(),
      version: '1.0',
    };

    return JSON.stringify(userData, null, 2);
  }

  /**
   * Import user data
   */
  async importUserData(dataString) {
    try {
      const userData = JSON.parse(dataString);
      
      // Import app data
      if (userData.assessment) {
        localStorage.setItem(`vibe_assessment_results_${this.currentUser?.userId || 'guest'}`, userData.assessment);
      }
      if (userData.gamification) {
        localStorage.setItem('vibeGamificationData', userData.gamification);
      }
      if (userData.progress) {
        localStorage.setItem('vibeUserProgress', userData.progress);
      }
      if (userData.settings) {
        localStorage.setItem(this.settingsKey, JSON.stringify(userData.settings));
      }

      return { success: true, message: 'Data imported successfully!' };
    } catch (error) {
      return { success: false, error: 'Invalid backup data' };
    }
  }

  /**
   * Logout
   */
 logout(keepData = true) {
  console.log('🚪 Logging out user, keepData:', keepData);
  
  this.currentUser = null;
  
  if (!keepData) {
    console.log('🧹 Clearing all app data including storage service...');
    
    // Step 1: Clear storage service data
    this.clearStorageServiceData();
    
    // Step 2: Clear all localStorage with comprehensive patterns
    this.clearAllLocalStorageData();
    
    // Step 3: Clear global context references
    this.clearGlobalContexts();
    
    console.log('✅ Complete data cleanup finished');
  } else {
    // Just remove auth-specific data
    localStorage.removeItem(this.storageKey);
    console.log('🔑 Removed auth data only');
  }
}

/**
 * End what the hub sync is holding for this browser.
 *
 * The anonymous hub identity belongs to the BROWSER, not the account, so a
 * local wipe on its own is undone on the next page load by hydration. Sign-out
 * therefore drops queued pushes (the data they refer to is being deleted),
 * clears the per-account tool entries, and signs out of both the hub app and
 * this project. The previous person's copy stays on the hub - it is theirs -
 * it is just no longer reachable from this browser.
 */
async clearStorageServiceData() {
  try {
    const sync = await import('../services/ToolSyncService');
    sync.resetSyncState();
  } catch { /* sync module unavailable: nothing queued */ }
  try {
    const { clearToolData } = await import('../utils/toolStorage');
    clearToolData();
  } catch { /* storage unavailable */ }
  try {
    const { endHubSession } = await import('../services/HubSessionService');
    await endHubSession();
  } catch { /* no hub session to end */ }
}

/**
 * Clear all localStorage data with comprehensive patterns
 */
clearAllLocalStorageData() {
  console.log('🗑️ Clearing localStorage data...');
  
  // Get all localStorage keys
  const allKeys = [];
  for (let i = 0; i < localStorage.length; i++) {
    allKeys.push(localStorage.key(i));
  }
  
  console.log('📋 All localStorage keys before cleanup:', allKeys);
  
  // Define comprehensive patterns for data that should be cleared
  const dataPatterns = [
    // Vibe-specific patterns
    'vibe_',
    'mev:',
    'mindease-vibe_',
    'mindease_vibe_',
    'mindease-vibe-gamification',
    'friendships-vibe-gamification',
    'friend-circles-storage',
    'vibeGamificationData',
    'vibeAssessmentData', 
    'vibeUserProgress',
    'vibe-daily-challenges',
    'daily_challenge_',
    
    // Friendships Vibe storage patterns
    'friendships_',
    'friendshipsVibe_',

    // Clone-era storage-service patterns (the layer is gone; keys may remain)
    'friendships_assessment_',
    'friendships_progress_',
    'friendships_modules_',
    'friendships_social-skills_',
    'friendships_profile_',
    'friendships_techniques_',
    'friendships_boundaries_',
    'friendships_connections_',
    'friendships_wellbeing_',
    'friendships_support_',
    'friendships_notes_',
    'friendships_analytics_',
    
    // Any other app-specific patterns
    'app_',
    'user_',
    'state_',
  ];
  
  const keysToRemove = [];
  
  // Check each key against our patterns
  allKeys.forEach(key => {
    const shouldRemove = dataPatterns.some(pattern => 
      key.startsWith(pattern) || key.includes(pattern),
    );
    
    // Also check for keys that contain specific app-related terms
    const containsAppData = [
      'module', 'progress', 'gamification', 'assessment', 'completed',
      'achievement', 'level', 'streak', 'xp', 'selfTalk', 'futureLetters',
    ].some(term => key.toLowerCase().includes(term));
    
    if (shouldRemove || containsAppData) {
      keysToRemove.push(key);
    }
  });
  
  console.log('🗑️ Keys to remove:', keysToRemove);
  
  // Remove all matching keys
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    console.log('❌ Removed:', key);
  });
  
  console.log(`✅ Removed ${keysToRemove.length} localStorage keys`);
}

/**
 * Clear global context references
 */
clearGlobalContexts() {
  console.log('🧹 Clearing global contexts...');
  
  // Clear gamification context
  if (window.__vibeGamificationContext__) {
    delete window.__vibeGamificationContext__;
    console.log('🧹 Cleared global gamification context');
  }
  
  // Clear any other global references that might exist
  const globalKeys = Object.keys(window).filter(key =>
    key.includes('vibe') || key.includes('friendships') || key.includes('gamification'),
  );
  
  globalKeys.forEach(key => {
    try {
      delete window[key];
      console.log(`🧹 Cleared global: ${key}`);
    } catch (error) {
      console.warn(`⚠️ Could not clear global: ${key}`, error);
    }
  });
  
  console.log('✅ Global contexts cleared');
}

/**
 * Force reload application (helper method)
 */
forceApplicationReload() {
  console.log('🔄 Force reloading application...');
  setTimeout(() => {
    window.location.reload();
  }, 500);
}

  /**
   * Delete account and all data
   */
  async deleteAccount() {
    try {
      await this.clearStorageServiceData();
      // Clear all data
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('vibe_') || key.startsWith('friendships-vibe_') || key.startsWith('mindease-vibe_') || key.startsWith('mindease_vibe_') || key.startsWith('mev:'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      this.currentUser = null;
      
      return { success: true, message: 'Account deleted successfully' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

// React Context
const PrivacyAuthContext = createContext();

export const PrivacyAuthProvider = ({ children }) => {
  // Debug check for React and hooks
  if (typeof React === 'undefined') {
    console.error('React is undefined');
    return <div>Error: React not available</div>;
  }

  if (typeof useState === 'undefined') {
    console.error('useState is undefined - React hooks not available');
    return <div>Error: React hooks not available</div>;
  }

  const [auth] = useState(new PrivacyFirstAuth());
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPassphrase, setHasPassphrase] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(null);

  useEffect(() => {
    console.log('🔍 PrivacyAuthContext: Initializing Firebase auth listener');

    // Listen for Firebase auth state changes
    const unsubscribe = onAuthStateChanged(firebaseAuth, (firebaseUser) => {
      console.log('🔍 Firebase auth state changed:', firebaseUser ? 'User signed in' : 'No user');
      setFirebaseUser(firebaseUser);

      if (firebaseUser) {
        // User is signed in via Firebase
        const userData = {
          userId: firebaseUser.uid,
          // An account with no email is the anonymous one the auth hub minted for
          // this browser (HubSessionService): a custom-token user, so Firebase
          // does not flag it isAnonymous itself. "Guest" is what it is.
          username: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Guest',
          email: firebaseUser.email,
          isAnonymous: firebaseUser.isAnonymous || !firebaseUser.email,
          accountType: firebaseUser.isAnonymous || !firebaseUser.email ? 'anonymous' : 'email',
        };
        console.log('🔥 Firebase user authenticated:', {
          displayName: firebaseUser.displayName,
          email: firebaseUser.email,
          uid: firebaseUser.uid,
          finalUsername: userData.username,
        });

        // IMPORTANT: Persist Firebase user to localStorage
        // This ensures the auth state survives page reloads
        localStorage.setItem('vibe_user_identity', JSON.stringify(userData));

        setUser(userData);
        setIsLoading(false);
      } else {
        // No Firebase user, check for local auth or cached user
        const initAuth = async () => {
          try {
            // First check if we have cached user data from a previous Firebase session
            const cachedUser = localStorage.getItem('vibe_user_identity');
            const authSuccess = localStorage.getItem('vibe_auth_success');

            if (cachedUser && authSuccess === 'true') {
              try {
                const userData = JSON.parse(cachedUser);
                console.log('📱 Found cached user data from successful auth:', userData);

                // CRITICAL FIX: Set the user immediately from cache
                // Firebase onAuthStateChanged may fire with null initially
                // But we know the user just logged in successfully
                setUser(userData);
                setIsLoading(false);
                return; // Exit early, we have our user
              } catch (e) {
                console.warn('Invalid cached user data');
                localStorage.removeItem('vibe_user_identity');
                localStorage.removeItem('vibe_auth_success');
              }
            }

            // Try auto-login for local accounts
            const autoLoginResult = await auth.autoLogin();
            if (autoLoginResult.success) {
              setUser({
                userId: autoLoginResult.userId,
                username: autoLoginResult.username,
              });
              setHasPassphrase(autoLoginResult.hasPassphrase);
            } else {
              // Check if user exists but needs passphrase
              const currentUser = auth.getCurrentUser();
              if (currentUser) {
                setHasPassphrase(!!currentUser.passphraseHash);
              }
            }
          } catch (error) {
            console.error('Auth initialization error:', error);
          } finally {
            setIsLoading(false);
          }
        };

        initAuth();
      }
    });
    
    return () => unsubscribe();
  }, [auth]);

  const quickSetup = useCallback(async (username) => {
    const result = await auth.quickSetup(username);
    if (result.success) {
      setUser({ userId: result.userId, username: result.username });
      setHasPassphrase(result.hasPassphrase);
    }
    return result;
  }, [auth]);

  const createAccount = useCallback(async (username, passphrase) => {
    const result = await auth.createAnonymousAccount(username, passphrase);
    if (result.success) {
      setUser({ userId: result.userId, username: result.username });
      setHasPassphrase(result.hasPassphrase);
    }
    return result;
  }, [auth]);

  const login = useCallback(async (username, passphrase = null) => {
    const result = await auth.login(username, passphrase);
    if (result.success) {
      setUser({ userId: result.userId, username: result.username });
      setHasPassphrase(result.hasPassphrase);
    }
    return result;
  }, [auth]);

  const addPassphrase = useCallback(async (passphrase) => {
    const result = await auth.addPassphrase(passphrase);
    if (result.success) {
      setHasPassphrase(true);
    }
    return result;
  }, [auth]);

  const logout = useCallback((keepData = true) => {
    auth.logout(keepData);
    setUser(null);
    setHasPassphrase(false);
  }, [auth]);

  const deleteAccount = useCallback(async () => {
    const result = await auth.deleteAccount();
    if (result.success) {
      setUser(null);
      setHasPassphrase(false);
    }
    return result;
  }, [auth]);

  // Memoize bound functions to prevent re-creation
  const exportData = useCallback(() => auth.exportUserData(), [auth]);
  const importData = useCallback((data) => auth.importUserData(data), [auth]);
  const updateSettings = useCallback((settings) => auth.updateSettings(settings), [auth]);
  const getSettings = useCallback(() => auth.getSettings(), [auth]);

  const value = useMemo(() => ({
    user,
    isLoading,
    isLoggedIn: !!user,
    hasPassphrase,
    quickSetup,
    createAccount,
    login,
    logout,
    addPassphrase,
    deleteAccount,
    exportData,
    importData,
    updateSettings,
    getSettings,
  }), [user, isLoading, hasPassphrase, quickSetup, createAccount, login, logout, addPassphrase, deleteAccount, exportData, importData, updateSettings, getSettings]);

  return (
    <PrivacyAuthContext.Provider value={value}>
      {children}
    </PrivacyAuthContext.Provider>
  );
};

export const usePrivacyAuth = () => {
  const context = useContext(PrivacyAuthContext);
  if (!context) {
    throw new Error('usePrivacyAuth must be used within PrivacyAuthProvider');
  }
  return context;
};

export default PrivacyAuthContext;